import { useState, useEffect } from 'react';
import { formatINR, formatDate } from '../../utils/formatters';
import axios from 'axios';

const portalApi = axios.create({ baseURL: '/api/v1/portal' });

export function VendorPortalPage() {
  const [token, setToken] = useState(localStorage.getItem('vendor_portal_token') || '');
  const [loggedIn, setLoggedIn] = useState(!!token);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await portalApi.post('/auth/login', { email, code });
      const t = res.data.data.token;
      localStorage.setItem('vendor_portal_token', t);
      portalApi.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      setToken(t);
      setLoggedIn(true);
    } catch { setError('Invalid email or access code'); }
  };

  useEffect(() => {
    if (token) portalApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, [token]);

  useEffect(() => {
    if (!loggedIn) return;
    setLoading(true);
    portalApi.get('/vendor/purchase-orders').then(r => setOrders(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [loggedIn]);

  const respond = async (id: string, action: 'accept' | 'reject') => {
    try {
      await portalApi.patch(`/vendor/purchase-orders/${id}/respond`, { action });
      setOrders(orders.map(o => o.id === id ? { ...o, status: action === 'accept' ? 'approved' : 'cancelled' } : o));
    } catch {}
  };

  const logout = () => { localStorage.removeItem('vendor_portal_token'); setLoggedIn(false); setToken(''); };

  if (!loggedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--ink-0)] text-xl font-bold text-white">A</div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Vendor Portal</h1>
            <p className="text-sm text-gray-500">Sign in to view and respond to purchase orders</p>
          </div>
          <form onSubmit={login} className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="mb-4"><label className="mb-1 block text-sm font-medium text-gray-700">Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" /></div>
            <div className="mb-6"><label className="mb-1 block text-sm font-medium text-gray-700">Access Code</label><input type="text" value={code} onChange={e => setCode(e.target.value)} required placeholder="6-digit code" className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:border-[var(--ink-0)] focus:outline-none tracking-widest text-center text-lg" maxLength={6} /></div>
            <button type="submit" className="w-full rounded-lg bg-[var(--ink-0)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)]">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3"><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--ink-0)] text-sm font-bold text-white">A</div><span className="text-lg font-bold text-gray-900">Vendor Portal</span></div>
          <button onClick={logout} className="text-sm text-gray-500 hover:text-red-600">Logout</button>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-6 py-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Purchase Orders</h2>
        {loading ? <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--ink-0)] border-t-transparent" /></div> : orders.length === 0 ? <p className="py-16 text-center text-sm text-gray-400">No purchase orders found</p> : (
          <div className="space-y-3">
            {orders.map((o: any) => (
              <div key={o.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-5">
                <div>
                  <p className="font-medium text-[var(--ink-0)]">{o.number}</p>
                  <p className="text-sm text-gray-500">{formatDate(o.issueDate)} · {formatINR(o.total)}</p>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${o.status === 'approved' ? 'bg-green-100 text-green-700' : o.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-700'}`}>{o.status}</span>
                </div>
                {o.status === 'sent' && (
                  <div className="flex gap-2">
                    <button onClick={() => respond(o.id, 'accept')} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">Accept</button>
                    <button onClick={() => respond(o.id, 'reject')} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
